import { css, useTheme } from "@emotion/react";
import { omit } from "lodash/fp";
import { forwardRef, useContext } from "react";

import backgroundColorContext from "./backgroundColorContext.js";
import BackgroundColorContextProvider from "./BackgroundColorContextProvider.jsx";
import getVisualColor from "./getVisualColor.js";
import parseStyle from "./parseStyle.js";
import useThemeProps from "./useThemeProps.jsx";

export default forwardRef(function Box(props, ref) {
  let {
    color = "auto",
    autoColor = "gray",
    padding = [4, 6],
    paddingBlock = padding,
    paddingInline = padding,
    borderRadius,
    children,
    height,
    shadow = false,
    keepPadding = shadow,
    style = {},
    as: Component = "div",
    ...restProps
  } = useThemeProps({ props: omit(["align"], props), name: "Box" });
  const theme = useTheme();

  let containerBackgroundColor = useContext(backgroundColorContext);

  const isBackgroundWhite =
    containerBackgroundColor === theme.getColor("white");
  if (color === "auto") {
    color = isBackgroundWhite ? autoColor : "white";
  }

  let visualColor = getVisualColor(
    containerBackgroundColor,
    theme.getColor(color),
  );
  let isColorSameAsContainer =
    visualColor && visualColor === containerBackgroundColor;

  let stripPadding = !keepPadding && isColorSameAsContainer;

  return (
    <BackgroundColorContextProvider backgroundColor={visualColor}>
      <Component
        css={css`
          ${theme.styleUtils.colorTheme(color, theme)}
          padding-block: ${stripPadding ? 0 : theme.getLength(paddingBlock)};
          padding-inline: ${stripPadding ? 0 : theme.getLength(paddingInline)};
          height: ${height != null && `${height}px`};
          border-radius: ${theme.getLength(borderRadius)};
          box-shadow: ${shadow && `0 .25rem .5rem rgba(0,0,0,.15)`};
          ${parseStyle(style, theme)}
        `}
        ref={ref}
        {...restProps}
      >
        {children}
      </Component>
    </BackgroundColorContextProvider>
  );
});
